// src/pages/SupportPage.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase/firestore";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { useUser } from "../context/userContext";
import EmojiPicker from "emoji-picker-react";
import { useNavigate } from "react-router-dom";

const SupportPage = () => {
  const navigate = useNavigate();
  const { id: userId, fullName, username } = useUser();
  const [message, setMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const [inputBottomPadding, setInputBottomPadding] = useState(0);

  // Telegram back button
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.BackButton.show();
    tg.BackButton.onClick(() => navigate(-1));

    return () => {
      tg.BackButton.hide();
      tg.BackButton.offClick();
    };
  }, [navigate]);

  // Load message history
  useEffect(() => {
    const loadMessages = async () => {
      const messagesRef = collection(db, `supportMessages/${userId}/messages`);
      const q = query(messagesRef, orderBy("createdAt", "asc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => doc.data());
      setMessages(data);
    };

    if (userId) loadMessages();
  }, [userId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Adjust input padding for keyboard resize
  useEffect(() => {
    const handleResize = () => {
      const vp = window.visualViewport;
      if (vp) {
        const heightDiff = window.innerHeight - vp.height;
        setInputBottomPadding(heightDiff > 0 ? heightDiff : 0);
      }
    };

    handleResize();
    window.visualViewport?.addEventListener("resize", handleResize);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSend = async () => {
    if (!message.trim()) return;

    const userRef = collection(db, `supportMessages/${userId}/messages`);

    await addDoc(userRef, {
      message,
      userId,
      fullName,
      username,
      createdAt: serverTimestamp(),
      from: "user",
    });

    setMessage("");
    setShowEmoji(false);

    const q = query(userRef, orderBy("createdAt", "asc"));
    const snapshot = await getDocs(q);
    const allMessages = snapshot.docs.map((doc) => doc.data());
    setMessages(allMessages);

    if (allMessages.length === 1) {
      const now = new Date();
      const mskOffset = 3 * 60;
      const localMsk = new Date(
        now.getTime() + (mskOffset - now.getTimezoneOffset()) * 60000
      );
      const hour = localMsk.getHours();

      const isWorkingHours = hour >= 10 && hour < 18;
      const autoReply = isWorkingHours
        ? "Thank you for reaching out! We’ll get back to you shortly."
        : "Working hours of customer manager : 9:00 - 20:00 UTC+2\nMessages received outside these hours will be answered the next day.";

      await addDoc(userRef, {
        message: autoReply,
        createdAt: serverTimestamp(),
        from: "admin",
      });

      const updatedSnapshot = await getDocs(q);
      const updatedMessages = updatedSnapshot.docs.map((doc) => doc.data());
      setMessages(updatedMessages);
    }
  };

  return (
    <div
      ref={containerRef}
      className="p-4 max-w-2xl mx-auto mt-6 text-text flex flex-col h-[calc(100vh-48px)]"
      style={{ paddingBottom: `${inputBottomPadding}px` }}
    >
      <h2 className="text-2xl font-bold mb-4">Support</h2>

      {/* Chat */}
      <div className="bg-white rounded-xl shadow flex-1 overflow-y-auto p-4 space-y-3 mb-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`max-w-[75%] p-3 rounded-lg text-sm ${
              msg.from === "admin"
                ? "bg-blue-100 ml-auto text-right"
                : "bg-gray-100 mr-auto text-left"
            }`}
          >
            <div>{msg.message}</div>
            <div className="text-xs text-gray-500 mt-1">
              {msg.from === "admin" ? "Support" : "You"}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Form */}
      <div className="bg-white p-4 rounded-xl shadow space-y-2">
        <textarea
          className="w-full border border-gray-300 rounded-lg p-3 resize-none min-h-[100px] focus:outline-none focus:ring"
          placeholder="Write your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onFocus={() => setShowEmoji(false)}
        />

        <div className="flex justify-between items-center">
          <button
            className="text-sm text-blue-500 hover:underline"
            onClick={() => setShowEmoji(!showEmoji)}
          >
            😊 Add emoji
          </button>

          <button
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
            onClick={handleSend}
          >
            Send
          </button>
        </div>

        {showEmoji && (
          <div className="mt-2">
            <EmojiPicker
              onEmojiClick={(e) => setMessage((prev) => prev + e.emoji)}
              height={300}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
